import {
  applyRemainderScheduleToCharges,
  generateOriginalInvoiceBalanceCharges,
  parseInvoiceRemainderSchedule,
} from "@/modules/billing/invoiceRemainderSchedule";

export type InvoiceCollectionDueInput = {
  due_date?: string | null;
  issue_date?: string | null;
  amount?: number | string | null;
  amount_paid?: number | string | null;
  upfront_percent?: number | string | null;
  auto_charge_remainder?: boolean | null;
  remainder_schedule?: unknown;
};

/**
 * The date the balance is actually collected. For a deposit invoice whose
 * deposit is paid and whose remainder auto-charges on a schedule, that is
 * the last scheduled charge (e.g. project end), not the invoice due date —
 * so the invoice is not "overdue" while the scheduled charge is pending.
 * Otherwise the invoice due date.
 */
export const getInvoiceCollectionDueDate = (
  invoice: InvoiceCollectionDueInput,
): string | null => {
  const dueDate = invoice.due_date?.slice(0, 10) || null;
  if (!dueDate || !invoice.auto_charge_remainder) return dueDate;

  const upfrontPercent = Number(invoice.upfront_percent ?? 100);
  if (!(upfrontPercent < 100)) return dueDate;

  const total = Number(invoice.amount) || 0;
  const paid = Number(invoice.amount_paid) || 0;
  const depositTarget =
    Math.round(total * (Math.max(upfrontPercent, 1) / 100) * 100) / 100;
  // Until the deposit is in, the deposit itself is due on the due date.
  if (paid < depositTarget - 0.01) return dueDate;

  const config = parseInvoiceRemainderSchedule(
    invoice.remainder_schedule,
    dueDate,
  );
  const charges = applyRemainderScheduleToCharges(
    generateOriginalInvoiceBalanceCharges({
      balanceAmount: Math.max(
        Math.round((total - depositTarget) * 100) / 100,
        0,
      ),
      config,
      invoiceDueDate: dueDate,
      issueDate: invoice.issue_date?.slice(0, 10) || dueDate,
    }),
    config,
  );
  const lastCharge = charges
    .map((charge) => charge.due_date)
    .sort()
    .at(-1);
  return lastCharge && lastCharge > dueDate ? lastCharge : dueDate;
};
